import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import Modal from 'react-native-modal';
import {Formik} from 'formik';

import config from '../../../../config';
import {useWebScreenDimensions} from '../../../../hooks/useWebScreenDimensions';
import useAppNavigation from '../../../../navigation/useAppNavigation';
import {useStores} from '../../../../stores/use-stores';
import Button from '../../../ui-controls/Button';
import TextInput from '../../../ui-controls/form/TextInput';
import Text from '../../../ui-controls/Text';
import {Title} from '../../../ui-controls/text/Title';

import CardTokeniserTillProps from './CardTokeniserTillProps';
import CardTokeniserTillSchema, {
  CardTokeniserTillValues,
  useCardTokeniserTillSchemaDefaultValues,
} from './CardTokeniserTillSchema';
import styles from './styles';
import useTillServiceIntegrationKeyEffect from './useTillServiceIntegrationKeyEffect';
import {
  createTillPaymentInstrumentAndShowToast,
  showToastIfTokeniserErrors,
} from './util';

// https://gateway.tillpayments.com/documentation/gateway#paymentjs-javascript-integration

type TillJsPaymentJs = {
  init: (
    integrationKey: string,
    numberDivId: string,
    cvvDivId: string,
    callback: (payment: any) => void,
  ) => void;
  tokenize: (
    data: any,
    successCallback: (token: any) => void,
    errorCallback: (errors: any) => void,
  ) => void;
  setNumberStyle: (style: any) => void;
  setCvvStyle: (style: any) => void;
  numberOn: (event: string, callback: (data: any) => void) => void;
  cvvOn: (event: string, callback: (data: any) => void) => void;
};

const CardTokeniserTillModal = ({
  onCancel = () => null,
  isVisible,
  navigationTarget,
}: CardTokeniserTillProps) => {
  const {paymentStore} = useStores();

  const navigation = useAppNavigation();
  const {integrationKey, setIntegrationKey} =
    useTillServiceIntegrationKeyEffect(isVisible);
  const defaultValues = useCardTokeniserTillSchemaDefaultValues();

  const [isActivityIndicatorVisible, setActivityIndicatorVisible] =
    useState(true);

  const [paymentJsScript, setPaymentJsScript] =
    useState<TillJsPaymentJs | null>(null);
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const formRef = useRef();

  // Attach the script to the document body
  useEffect(() => {
    let script: HTMLScriptElement;
    if (isVisible) {
      script = document.createElement('script');
      script.src = config.TILL_GATEWAY;
      script.async = true;
      script.setAttribute('data-main', 'payment-js');
      script.addEventListener('load', () =>
        // eslint-disable-next-line no-undef
        setPaymentJsScript(new PaymentJs()),
      );
      document.body.appendChild(script);
    }

    return () => {
      if (script) {
        setPaymentJsScript(null);
        document.body.removeChild(script);
        // Till injects another script to handle the iFrames injected into the divs, clean those up.
        const tillVault = document.body.querySelector('[data-main="vault-js"]');
        if (tillVault) {
          document.body.removeChild(tillVault);
        }
      }
    };
  }, [isVisible]);

  useEffect(() => {
    const handleNumberInput = (data: string) => ccFieldInput('number', data);
    const handleNumberBlur = () => formRef?.current?.setFieldTouched('number');
    const handleCvvInput = (data: string) => ccFieldInput('cvv', data);
    const handleCvvBlur = () => formRef?.current?.setFieldTouched('cvv');

    if (paymentJsScript && integrationKey && isVisible) {
      paymentJsScript.init(
        integrationKey,
        'number_div',
        'cvv_div',
        function (payment) {
          let inputStyle = {
            width: '100%',
            height: 32,
            'font-weight': '400',
            'font-size': '18px',
            'border-radius': '0',
            'border-color': 'rgb(134, 147, 158)',
            'border-bottom-width': '1px',
            'border-top-width': '0',
            'border-left-width': '0',
            'border-right-width': '0',
            'padding-left': '4',
            'padding-right': '4',
          };
          payment.setNumberStyle(inputStyle);
          payment.setCvvStyle(inputStyle);

          payment.numberOn('input', handleNumberInput);
          payment.numberOn('blur', handleNumberBlur);
          payment.cvvOn('input', handleCvvInput);
          payment.cvvOn('blur', handleCvvBlur);
          setActivityIndicatorVisible(false);
        },
      );
    }
  }, [paymentJsScript, integrationKey, isVisible]);

  const ccFieldInput = (field: string, data: any) =>
    formRef?.current?.setFieldValue(field, data.validNumber || null);

  const onSubmit = () => {
    setActivityIndicatorVisible(true);

    const data = {
      card_holder: formRef.current.values.cardHolder,
      month: formRef.current.values.month,
      year: formRef.current.values.year,
    };

    paymentJsScript.tokenize(
      data, // This holds the data from our react forms
      function (token: any) {
        // Success callback
        createTillPaymentInstrumentAndShowToast({
          token,
          onCancel,
          navigationTarget,
          navigation,
          paymentStore,
        });
      },
      function (errors) {
        // Error callback
        showToastIfTokeniserErrors(errors);
        setActivityIndicatorVisible(false);
      },
    );
  };

  const onClose = () => {
    setIntegrationKey?.(null);
    setPaymentJsScript(null);
    setActivityIndicatorVisible(true);
    onCancel();
  };

  return (
    <Modal
      isVisible={isVisible}
      avoidKeyboard={true}
      onBackdropPress={onClose}
      onBackButtonPress={onClose}
      onModalHide={onClose}
      style={isWebDesktopScreen ? styles.webDesktopModalContent : {}}>
      <View testID="card-tokeniser-till-web-modal-content">
        {!!isActivityIndicatorVisible && (
          <ActivityIndicator size="large" style={styles.activity} />
        )}
        <Formik<CardTokeniserTillValues>
          initialValues={defaultValues}
          onSubmit={onSubmit}
          validationSchema={CardTokeniserTillSchema}
          innerRef={formRef}>
          {({
            handleBlur,
            handleChange,
            handleSubmit,
            values,
            errors,
            touched,
            // Do not remove these two they are referenced via the object reference outside
            // of the formik context
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            setFieldValue,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            setFieldTouched,
            resetForm,
          }) => {
            const validationMessage = (field: string) => (
              <Text style={styles.validationError}>
                {(touched[field] && errors[field]) || ' '}
              </Text>
            );

            const clearForm = () => {
              resetForm();
              onClose();
            };

            return (
              <KeyboardAwareScrollView
                style={styles.keyAvoidScroll}
                keyboardShouldPersistTaps="always">
                <View style={styles.form}>
                  <Title>Add new card</Title>
                  <TextInput
                    autoCapitalize="none"
                    errorMessageWhenTouched={errors.cardHolder}
                    label="Cardholder name"
                    onBlur={handleBlur('cardHolder')}
                    onChangeText={handleChange('cardHolder')}
                    returnKeyLabel="next"
                    returnKeyType="next"
                    touched={touched.cardHolder}
                    value={values.cardHolder}
                  />
                  <Text style={styles.label}>Card number</Text>
                  <View style={styles.tillFieldsWrapper}>
                    {/* This div is controlled by Till */}
                    <div
                      tabIndex={0}
                      id="number_div"
                      style={styles.tillFields}
                    />
                  </View>
                  {validationMessage('number')}
                  <Text style={styles.label}>CVV</Text>
                  <View style={styles.tillFieldsWrapper}>
                    {/* This div is controlled by Till */}
                    <div tabIndex={1} id="cvv_div" style={styles.tillFields} />
                  </View>
                  {validationMessage('cvv')}
                  <View style={styles.formRow}>
                    <TextInput
                      autoCapitalize="none"
                      containerStyle={styles.formRowField}
                      inputStyle={{width: '100%'}}
                      errorMessageWhenTouched={errors.month}
                      keyboardType="number-pad"
                      label="Expiry month"
                      maxLength={2}
                      onBlur={handleBlur('month')}
                      onChangeText={handleChange('month')}
                      placeholder="MM"
                      returnKeyLabel="next"
                      returnKeyType="next"
                      touched={touched.month}
                      value={values.month}
                    />
                    <TextInput
                      inputStyle={{width: '100%'}}
                      autoCapitalize="none"
                      containerStyle={styles.formRowField}
                      errorMessageWhenTouched={errors.year}
                      keyboardType="number-pad"
                      label="Expiry year"
                      maxLength={2}
                      onBlur={handleBlur('year')}
                      onChangeText={handleChange('year')}
                      placeholder="YY"
                      returnKeyLabel="next"
                      returnKeyType="next"
                      touched={touched.year}
                      value={values.year}
                    />
                  </View>
                  <View style={styles.buttonRow}>
                    <Button
                      title="ADD CARD"
                      onPress={handleSubmit}
                      buttonStyle={styles.button}
                    />
                    <Button
                      type="outline"
                      title="CANCEL"
                      onPress={clearForm}
                      buttonStyle={styles.button}
                    />
                  </View>
                </View>
              </KeyboardAwareScrollView>
            );
          }}
        </Formik>
      </View>
    </Modal>
  );
};

export default CardTokeniserTillModal;
